
import { useState, useEffect } from 'react';
import { fetchModules } from '@/services/modules/moduleService';
import { LearningModule, TaxonomyLevel } from '@/types/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, HelpCircle, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const taxonomyLevels: TaxonomyLevel[] = [
  'remember',
  'understand',
  'apply',
  'analyze',
  'evaluate',
  'create'
];

const taxonomyColorMap: Record<TaxonomyLevel, string> = {
  'remember': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  'understand': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
  'apply': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
  'analyze': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
  'evaluate': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  'create': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100'
};

// Information about each taxonomy level
const taxonomyInfo: Record<TaxonomyLevel, { description: string, keywords: string[] }> = {
  'remember': {
    description: 'Recall facts and basic concepts',
    keywords: ['define', 'list', 'recall', 'name', 'identify']
  },
  'understand': {
    description: 'Explain ideas or concepts',
    keywords: ['explain', 'describe', 'discuss', 'interpret', 'summarize']
  },
  'apply': {
    description: 'Use information in new situations',
    keywords: ['implement', 'use', 'demonstrate', 'execute', 'solve']
  },
  'analyze': {
    description: 'Draw connections among ideas',
    keywords: ['differentiate', 'organize', 'compare', 'examine', 'test']
  },
  'evaluate': {
    description: 'Justify a stand or decision',
    keywords: ['judge', 'critique', 'defend', 'appraise', 'value']
  },
  'create': {
    description: 'Produce new or original work',
    keywords: ['design', 'construct', 'plan', 'produce', 'develop']
  }
};

export const TaxonomyPathVisualizer = () => {
  const [modules, setModules] = useState<LearningModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadModules = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchModules();
        setModules(data);
      } catch (err) {
        console.error("Error fetching modules:", err);
        setError("Failed to load modules data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    loadModules();
  }, []);

  const getTaxonomyModules = (level: TaxonomyLevel) => {
    return modules.filter(module => 
      module.taxonomy_level && 
      module.taxonomy_level.includes(level)
    );
  };

  const handleEditModule = (moduleId: string) => {
    navigate('/admin/course-builder', { state: { selectedTab: 'modules', moduleId } });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const modulesWithTaxonomy = modules.filter(module => 
    module.taxonomy_level && module.taxonomy_level.length > 0
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Bloom's Taxonomy Learning Path</CardTitle>
          <CardDescription>
            Visualization of modules mapped to Bloom's Taxonomy levels
          </CardDescription>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setShowInfo(!showInfo)}
          aria-label="Show taxonomy information"
        >
          <HelpCircle className="h-5 w-5" />
        </Button>
      </CardHeader>
      <CardContent>
        {showInfo && (
          <Alert className="mb-6">
            <Info className="h-4 w-4" />
            <AlertTitle>About Bloom's Taxonomy</AlertTitle>
            <AlertDescription>
              <p className="mb-2">Bloom's Taxonomy is a framework that categorizes educational goals into six cognitive levels of complexity:</p>
              <ol className="list-decimal ml-6 space-y-1">
                {taxonomyLevels.map(level => (
                  <li key={`info-${level}`}>
                    <span className="font-medium">{level.charAt(0).toUpperCase() + level.slice(1)}</span>: {taxonomyInfo[level].description}
                  </li>
                ))}
              </ol>
              <p className="mt-2">
                You can assign taxonomy levels to modules by editing them in the Modules tab, or through Learning Objectives.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {modulesWithTaxonomy.length === 0 ? (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>No Taxonomy Data</AlertTitle>
              <AlertDescription>
                <p>No modules have been assigned taxonomy levels yet.</p>
                <p className="mt-2">To add taxonomy levels to modules:</p>
                <ol className="list-decimal ml-6 mt-2 space-y-1">
                  <li>Go to the Modules tab</li>
                  <li>Edit a module</li>
                  <li>Select taxonomy levels from the checkboxes at the bottom of the form</li>
                </ol>
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              {taxonomyLevels.map((level) => (
                <div key={level} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={taxonomyColorMap[level]}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      0 modules
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground italic">No modules at this level</p>
                  <Separator />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {taxonomyLevels.map((level) => {
              const levelModules = getTaxonomyModules(level);
              return (
                <div key={level} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={taxonomyColorMap[level]}>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {levelModules.length} {levelModules.length === 1 ? 'module' : 'modules'}
                    </span>
                  </div>
                  
                  {levelModules.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {levelModules.map((module) => (
                        <div 
                          key={`${level}-${module.id}`} 
                          className="p-2 border rounded-md bg-background hover:bg-accent transition-colors cursor-pointer"
                          onClick={() => handleEditModule(module.id)}
                        >
                          <p className="font-medium truncate">{module.title}</p>
                          {module.phase && (
                            <p className="text-xs text-muted-foreground">{module.phase}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No modules at this level</p>
                  )}
                  
                  <Separator />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TaxonomyPathVisualizer;
